import React, { useState, useRef, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Camera, X, RefreshCw, Check, Loader2 } from 'lucide-react';

/**
 * CameraCapture Component
 * Captures photos using device camera (webcam on desktop, front/back on mobile)
 * 
 * Props:
 * - isOpen: boolean - whether the camera modal is open
 * - onClose: () => void - callback to close the modal
 * - onCapture: (file: File, previewUrl: string) => void - callback with captured image
 * - aspectRatio: string - aspect ratio for the camera view (default: "3/4")
 */
const CameraCapture = ({ isOpen, onClose, onCapture, aspectRatio = "3/4" }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [facingMode, setFacingMode] = useState('user'); // 'user' = front, 'environment' = back
  const [capturedImage, setCapturedImage] = useState(null);
  const [captureWarning, setCaptureWarning] = useState(null); // Warning for non-ideal capture
  const [hasMultipleCameras, setHasMultipleCameras] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Detect if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      const mobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      setIsMobile(mobile);
      // On mobile, default to back camera for better photos
      if (mobile) {
        setFacingMode('environment');
      }
    };
    checkMobile();
  }, []);

  // Check for multiple cameras
  useEffect(() => {
    const checkCameras = async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = devices.filter(device => device.kind === 'videoinput');
        setHasMultipleCameras(videoDevices.length > 1);
      } catch (err) {
        console.log('Could not enumerate devices:', err);
      }
    };
    if (isOpen) {
      checkCameras();
    }
  }, [isOpen]);

  // Start camera stream
  const startCamera = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    // Stop any existing stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
    
    try {
      const constraints = {
        video: {
          facingMode: facingMode,
          width: { ideal: 1080 },
          height: { ideal: 1440 },
        },
        audio: false
      };
      
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => {
          videoRef.current.play();
          setIsLoading(false);
        };
      }
    } catch (err) {
      console.error('Camera error:', err);
      if (err.name === 'NotAllowedError') {
        setError('Camera access denied. Please allow camera permissions in your browser settings.');
      } else if (err.name === 'NotFoundError') {
        setError('No camera found on this device.');
      } else if (err.name === 'NotReadableError') {
        setError('Camera is being used by another application.');
      } else {
        setError('Could not access camera. Please try again.');
      }
      setIsLoading(false);
    }
  }, [facingMode]);

  // Start camera when modal opens
  useEffect(() => {
    if (isOpen) {
      setCapturedImage(null);
      startCamera();
    }
    
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, [isOpen, startCamera]);

  // Switch between front and back camera
  const switchCamera = useCallback(() => {
    setCapturedImage(null);
    setFacingMode(prev => prev === 'user' ? 'environment' : 'user');
  }, []);

  // Capture photo
  const capturePhoto = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    
    // Get video dimensions
    const videoWidth = video.videoWidth;
    const videoHeight = video.videoHeight;
    
    // Check for non-ideal capture - only warn if landscape (width significantly > height)
    setCaptureWarning(null);
    if (videoWidth > videoHeight * 1.2) {
      // Only show warning if clearly landscape orientation
      setCaptureWarning('Tip: Portrait photos work best for full-body try-ons.');
    }
    
    // Calculate target dimensions (max 1024px on longest side for API compatibility)
    const maxDimension = 1024;
    let targetWidth, targetHeight;
    
    if (videoWidth > videoHeight) {
      // Landscape
      targetWidth = Math.min(videoWidth, maxDimension);
      targetHeight = Math.round((videoHeight / videoWidth) * targetWidth);
    } else {
      // Portrait
      targetHeight = Math.min(videoHeight, maxDimension);
      targetWidth = Math.round((videoWidth / videoHeight) * targetHeight);
    }
    
    // Set canvas to target dimensions (this resizes the image)
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    
    // Draw the video frame to canvas with resizing
    // Mirror the image if using front camera
    if (facingMode === 'user') {
      context.translate(canvas.width, 0);
      context.scale(-1, 1);
    }
    context.drawImage(video, 0, 0, targetWidth, targetHeight);
    
    // Reset transform
    context.setTransform(1, 0, 0, 1, 0, 0);
    
    // Get the image data URL with good compression (0.85 quality is good balance)
    const imageDataUrl = canvas.toDataURL('image/jpeg', 0.85);
    setCapturedImage(imageDataUrl);
  }, [facingMode]);

  // Retake photo
  const retakePhoto = useCallback(() => {
    setCapturedImage(null);
    setCaptureWarning(null);
  }, []);

  // Confirm and use the captured photo
  const confirmPhoto = useCallback(async () => {
    if (!capturedImage) return;
    
    try {
      // Convert data URL to blob
      const response = await fetch(capturedImage);
      const blob = await response.blob();
      
      // Log file size for debugging
      console.log(`Camera capture: ${(blob.size / 1024).toFixed(1)}KB`);
      
      // Create file with proper JPEG type
      const file = new File([blob], `camera-capture-${Date.now()}.jpg`, { 
        type: 'image/jpeg',
        lastModified: Date.now()
      });
      
      onCapture(file, capturedImage);
      onClose();
    } catch (error) {
      console.error('Error processing captured image:', error);
    }
  }, [capturedImage, onCapture, onClose]);

  // Handle close
  const handleClose = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
    setCapturedImage(null);
    onClose();
  }, [onClose]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
        onClick={handleClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="relative bg-slate-900 rounded-2xl overflow-hidden max-w-md w-full"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-slate-700">
            <h3 className="text-white font-semibold flex items-center gap-2">
              <Camera className="w-5 h-5" />
              {capturedImage ? 'Review Photo' : 'Take Photo'}
            </h3>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-slate-700 rounded-full transition-colors"
            >
              <X className="w-5 h-5 text-slate-400" />
            </button>
          </div>

          {/* Camera View / Preview */}
          <div className="relative" style={{ aspectRatio }}>
            {isLoading && !error && (
              <div className="absolute inset-0 flex items-center justify-center bg-slate-800">
                <div className="text-center">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-500 mx-auto mb-2" />
                  <p className="text-slate-400 text-sm">Starting camera...</p>
                </div>
              </div>
            )}
            
            {error && (
              <div className="absolute inset-0 flex items-center justify-center bg-slate-800 p-6">
                <div className="text-center">
                  <Camera className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                  <p className="text-slate-300 text-sm mb-4">{error}</p>
                  <button
                    onClick={startCamera}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm transition-colors"
                  >
                    Try Again
                  </button>
                </div>
              </div>
            )}

            {/* Live Video Feed */}
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className={`w-full h-full object-cover ${capturedImage ? 'hidden' : ''} ${facingMode === 'user' ? 'scale-x-[-1]' : ''}`}
            />

            {/* Full-Body Framing Overlay Guide */}
            {!capturedImage && !isLoading && !error && (
              <div className="absolute inset-0 pointer-events-none">
                {/* Simple rectangle frame guide - centered */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div 
                    className="relative"
                    style={{ 
                      width: '82%', 
                      height: '94%',
                    }}
                  >
                    {/* Frame SVG overlay */}
                    <svg 
                      className="absolute inset-0 w-full h-full"
                      viewBox="0 0 100 200"
                      preserveAspectRatio="none"
                    >
                      {/* Main rectangle frame */}
                      <rect 
                        x="2" y="2" 
                        width="96" height="196" 
                        rx="6" ry="6"
                        fill="none" 
                        stroke="rgba(255,255,255,0.4)" 
                        strokeWidth="1"
                      />
                      
                      {/* Corner brackets for framing - more prominent */}
                      <g stroke="rgba(255,255,255,0.8)" strokeWidth="2.5" fill="none">
                        {/* Top left */}
                        <path d="M 2 25 L 2 2 L 25 2" />
                        {/* Top right */}
                        <path d="M 98 25 L 98 2 L 75 2" />
                        {/* Bottom left */}
                        <path d="M 2 175 L 2 198 L 25 198" />
                        {/* Bottom right */}
                        <path d="M 98 175 L 98 198 L 75 198" />
                      </g>
                    </svg>
                  </div>
                </div>
              </div>
            )}

            {/* Captured Image Preview */}
            {capturedImage && (
              <img
                src={capturedImage}
                alt="Captured"
                className="w-full h-full object-cover"
              />
            )}

            {/* Hidden canvas for capture */}
            <canvas ref={canvasRef} className="hidden" />

            {/* Camera switch button (only on mobile with multiple cameras) */}
            {hasMultipleCameras && !capturedImage && !isLoading && !error && (
              <button
                onClick={switchCamera}
                className="absolute top-4 right-4 p-3 bg-black/50 hover:bg-black/70 rounded-full transition-colors"
                title={facingMode === 'user' ? 'Switch to back camera' : 'Switch to front camera'}
              >
                {/* Camera flip/switch icon */}
                <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M11 19H4a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h5" />
                  <path d="M13 5h7a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2h-5" />
                  <circle cx="12" cy="12" r="3" />
                  <path d="m18 22-3-3 3-3" />
                  <path d="m6 2 3 3-3 3" />
                </svg>
              </button>
            )}
          </div>

          {/* Controls */}
          <div className="p-4 border-t border-slate-700">
            {capturedImage ? (
              <div className="flex flex-col items-center gap-3">
                {/* Warning for non-ideal capture (non-blocking) */}
                {captureWarning && (
                  <div className="flex items-center gap-2 px-3 py-2 bg-amber-500/20 border border-amber-500/30 rounded-lg">
                    <svg className="w-4 h-4 text-amber-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <p className="text-amber-200 text-xs">{captureWarning}</p>
                  </div>
                )}
                
                <div className="flex items-center justify-center gap-4">
                  <button
                    onClick={retakePhoto}
                    className="flex items-center gap-2 px-6 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-xl transition-colors"
                  >
                    <RefreshCw className="w-5 h-5" />
                    Retake
                  </button>
                  <button
                    onClick={confirmPhoto}
                    className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-colors"
                  >
                    <Check className="w-5 h-5" />
                    Use Photo
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center">
                <button
                  onClick={capturePhoto}
                  disabled={isLoading || error}
                  className="w-16 h-16 bg-white hover:bg-slate-100 rounded-full flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                >
                  <div className="w-14 h-14 bg-white border-4 border-slate-900 rounded-full" />
                </button>
              </div>
            )}
            
            {/* Instructions - shown below capture button when camera is active */}
            {!capturedImage && !isLoading && !error && (
              <div className="mt-3 text-center">
                <p className="text-slate-400 text-xs">
                  Full body (head-to-toe) • Face camera • Good lighting
                </p>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CameraCapture;
